/**
 * Trilingual UI strings for the settings page chrome (legacy `frontend/`).
 *
 * Covers only the shell that `UserSettings.tsx` owns: page heading, tab
 * labels, and the aria page-title passed to `ScrollableTabsList`. The
 * individual panels (`UserSettingsProfile`, `UserSettingsPrivacy`, …) are
 * tracked separately as progressive-alignment follow-ups and will migrate
 * to `next-intl` once the surface is ported to the Next App Router per
 * `docs/migration/cra-to-nextjs-parity.md`.
 */
export type UserSettingsLocale = "en-US" | "ja-JP" | "ko-KR"

interface UserSettingsStrings {
    pageTitle: string
    tabProfile: string
    tabPrivacy: string
    tabAccount: string
    tabNotifications: string
}

const STRINGS: Record<UserSettingsLocale, UserSettingsStrings> = {
    "en-US": {
        pageTitle: "Settings",
        tabProfile: "Profile",
        tabPrivacy: "Privacy",
        tabAccount: "Account",
        tabNotifications: "Notifications",
    },
    "ja-JP": {
        pageTitle: "設定",
        tabProfile: "プロフィール",
        tabPrivacy: "プライバシー",
        tabAccount: "アカウント",
        tabNotifications: "通知",
    },
    "ko-KR": {
        pageTitle: "설정",
        tabProfile: "프로필",
        tabPrivacy: "개인정보",
        tabAccount: "계정",
        tabNotifications: "알림",
    },
}

/**
 * Resolves a supported locale from the browser, mirroring the fallback
 * behavior used by the other `i18n/*Ui.ts` helpers in this folder.
 */
export function resolveUserSettingsLocale(): UserSettingsLocale {
    if (typeof navigator === "undefined" || !navigator.language) {
        return "en-US"
    }
    const lang = navigator.language.toLowerCase()
    if (lang.startsWith("ja")) {
        return "ja-JP"
    }
    if (lang.startsWith("ko")) {
        return "ko-KR"
    }
    return "en-US"
}

export function getUserSettingsUiStrings(
    locale?: UserSettingsLocale
): UserSettingsStrings {
    const key = locale ?? resolveUserSettingsLocale()
    return STRINGS[key] ?? STRINGS["en-US"]
}
